/**
 * Provides an interface for selecting the layers of a map to be included in a spatial or primary key search.
 * The interface includes a tree control with check boxes for intuitively showing the child layers of the services on which searching is available. This tree control shall handle propagation in the following way:
 * * The "checking" of a child node shall propagate "checks" to all parent nodes between the child and root, but this check state shall *not* propagate to siblings of nodes at any level.
 * * The "checking" of a parent node shall propagate "checks", recursively, to all child nodes.
 * * The layers that are checked represent those layers to be included in the search.
 *
 * ## Events
 * Name | Arguments | Description
 * --- | --- | ---
 * selection-updated | None. | Fires whenever the value returned by `getSelectedLayers` is updated.
 * search-complete | The `MapQuery` that was run and the array of features found by the query. | Fires whenever a key-based search completes.
 * @module app/views/map/SearchPanelView
 */
import Backbone from "backbone";
import _ from "underscore";
import $ from "jquery";
import FormInputView from "core/views/FormInputView";
import ButtonView from "core/views/ButtonView";
import Field from "core/models/Field";
import ErrorUtils from "app/utils/ErrorUtils";
import TabView from "core/views/TabView";
import BasicView from "core/views/BasicView";
import QueryModel from "app/models/map/query/QueryModel";
import "jstree-bootstrap-theme/dist/themes/default/style.min.css";
import "jstree-bootstrap-theme/dist/themes/proton/style.min.css";
import "../../../../styles/specific/jstree.css";
import ViewHelper from "app/views/map/helpers/ViewHelper";
import cms from "core/context-menu-service";
import isMobile from "core/is-mobile";
import mapContext from "app/views/map/MapContext";
export default Backbone.View.extend({
  className: "search-panel",

  /**
   * Initializes the view.
   * @param  {object} options The options hash.
   * @param {Array} options.serviceLayers The array of `MapServiceBase` instances to include in the interface.
   * @param {string} options.title The label for the overall UI block containing this view.
   * @param {Boolean} options.allowKeySearch:true True to allow users to search by primary key on objects in the selected search layers, otherwise false.
   * @param {Boolean} options.allowSearch:true True to allow users to search by address, coordinate, etc., otherwise false.
   * @param {MapIndex} options.mapContainer The map container associated with this view.
   * @param {mixed} options.context The map context associated with this view.
   * @param {Layout} options.config The layer configuration to monitor for updates.
   */
  initialize: function (options) {
    this.options = _.defaults({}, options, {
      serviceLayers: [],
      allowSearch: true,
      allowKeySearch: true,
      mapContainer: null,
      title: "Search",
    });

    this._onServiceAdded = _.bind(function (service, index) {
      if (service) {
        return this.addServiceLayer(service, index);
      } else {
        return null;
      }
    }, this);

    this._onServiceRemoved = _.bind(function (service) {
      this.removeServiceLayer(service);
    }, this);

    this._onServiceMoved = _.bind(function (service, oldIndex, newIndex) {
      this._tree.move_node(this._nodeOf(service), "#", newIndex);
    }, this);

    this._onLayerAdded = _.bind(function (service, parentLayer, layer, index) {
      var parentNode = this._nodeOf(parentLayer || service);

      if (!this._nodeOf(layer)) {
        //need to make sure it doesn't get added twice
        if (layer.serviceLayer) {
          this._addLayerToTree(layer, parentNode, index);
        }
      }
    }, this);

    this._onLayerRemoved = _.bind(function (service, parentLayer, layer) {
      this.removeLayer(layer);
    }, this);

    this._onLayerMoved = _.bind(function (
      service,
      parent,
      layer,
      oldIndex,
      newIndex
    ) {
      var parentNode = this._nodeOf(parent || service);

      this._tree.move_node(this._nodeOf(layer), parentNode, newIndex);
    },
    this);
  },

  render: function () {
    var self = this;

    this._basicSearch = new BasicView({
      template: _.template('<div class="search-set"></div>'),
    });
    this._basicSearch.render();
    this._advancedSearch = new BasicView({
      template: _.template('<div class="field-container"></div>'),
    });
    this._advancedSearch.render();
    this._tabView = new TabView({
      tabs: [
        {
          view: this._basicSearch,
          title: "Basic",
        },
        {
          view: this._advancedSearch,
          title: "Advanced",
        },
      ],
      prefix: "search-tab",
    });

    this._tabView.on("start", function (tab, tabIndex) {
      if (tabIndex == 0) {
        var treeEl = self._basicSearch.$(".search-set");

        treeEl.jstree({
          core: {
            check_callback: true,
            themes: {
              name: "proton",
              responsive: true,
            },
          },
          checkbox: {
            tie_selection: false,
            whole_node: false,
          },
          plugins: ["checkbox"],
        });

        self._tree = $.jstree.reference(treeEl);
        treeEl.on("check_node.jstree", _.bind(self._onCheckNode, self));
        treeEl.on("uncheck_node.jstree", _.bind(self._onUnCheckNode, self));
      }
    });

    var searchField = new Field({
      id: "value",
      name: "value",
      label: "Keyword Search",
      editor: "text",
    });
    var searchModel = {
      value: "",
    };
    var searchInputView = new FormInputView({
      model: searchModel,
      field: searchField,
    });
    var searchButtonView = new ButtonView({
      text: "Search",
      className: isMobile ? "btn-lg" : "",
      click: function () {
        var queryModel = new QueryModel();

        mapContext
          .getOperation("basic-search")
          .action(queryModel, {
            field: searchField,
            searchModel: searchInputView.model,
          })
          .fail(
            ErrorUtils.errorHandler({
              activity: "running the query",
            })
          );
      },
    });

    searchInputView.render();
    searchButtonView.render();

    this._basicSearch.$el.append(searchInputView.$el);
    this._basicSearch.$el.append(searchButtonView.$el);

    this.setConfiguration(mapContext.getConfiguration());
    this._renderAdvancedSearch(this._advancedSearch);
    this._tabView.render();
    this.$el.append(this._tabView.$el);
  },

  /**
   * Gets the configuration associated with this view.
   * @return {Layout} The configuration.
   */
  getConfiguration: function () {
    return this._config;
  },

  /**
   * Sets the configuration associated with this view.
   * @param {Layout} config The configuration.
   */
  setConfiguration: function (config) {
    if (this._config) {
      _.each(
        this._config.serviceLayers,
        function (serviceLayer) {
          this.removeServiceLayer(serviceLayer);
        },
        this
      );

      this.stopListening(this._config);
    }

    this._config = config;

    if (this._config) {
      _.each(this._config.serviceLayers, this._onServiceAdded);

      this.listenTo(this._config, "service-added", this._onServiceAdded);
      this.listenTo(this._config, "service-removed", this._onServiceRemoved);
      this.listenTo(this._config, "service-moved", this._onServiceMoved);
      this.listenTo(this._config, "layer-added", this._onLayerAdded);
      this.listenTo(this._config, "layer-removed", this._onLayerRemoved);
    }
  },

  _renderAdvancedSearch: function (view) {
    var searchModel,
      inputsByKey,
      queryModel = new QueryModel(),
      self = this;

    var buttonView = new ButtonView({
      text: "Search",
      className: isMobile ? "btn-lg" : "",
      click: function () {
        mapContext
          .getOperation("advanced-search")
          .action(queryModel, { searchModel: searchModel })
          .fail(
            ErrorUtils.errorHandler({
              activity: "running the advanced query",
            })
          );
      },
    });

    buttonView.render();

    var updateSearchFields = function () {
      view.$el.children().remove();

      var searchFields = queryModel.getSearchFields() || [];

      searchModel = new Backbone.Model();
      inputsByKey = {};
      _.each(
        searchFields,
        function (searchField) {
          var formInputView = new FormInputView({
            model: searchModel,
            field: searchField,
          });

          inputsByKey[searchField.get("label")] = formInputView;
          formInputView.render();
          formInputView.$el.appendTo(view.$el);
        },
        this
      );

      view.$el.append(buttonView.$el);
      buttonView.delegateEvents();
    };

    updateSearchFields = _.bind(updateSearchFields, this);
    mapContext.getConfiguration().on("update-selection", updateSearchFields);
    updateSearchFields();
  },

  _onUnCheckNode: function (evt, args) {
    if (args.node.data) {
      if (args.node.data.layer) {
        this.getConfiguration().unselectLayer(args.node.data.layer);
      }
    }

    var newArgs = _.extend({}, args);

    _.each(
      args.node.children,
      _.bind(function (childId) {
        newArgs.node = this._tree.get_node(childId);
        newArgs.bubbling = true;
        this._onUnCheckNode(evt, newArgs);
      }, this)
    );

    if (!args.bubbling) {
      this.trigger("selection-updated");
    }
  },

  _onCheckNode: function (evt, args) {
    if (args.node.data) {
      if (args.node.data.layer) {
        if (args.node.data.layer.fields)
          this.getConfiguration().selectLayer(args.node.data.layer);
      }
    }

    var newArgs = _.extend({}, args);

    _.each(
      args.node.children,
      _.bind(function (childId) {
        newArgs.node = this._tree.get_node(childId);
        newArgs.bubbling = true;
        this._onCheckNode(evt, newArgs);
      }, this)
    );

    if (!args.bubbling) {
      this.trigger("selection-updated");
    }
  },

  events: {
    "check_node.jstree .search-set": "_onCheckNode",
    "uncheck_node.jstree .search-set": "_onUnCheckNode",
  },

  /**
   * Adds a service to the interface.
   * @param {MapServiceBase} serviceLayer The service to add.
   * @param {Integer} [index]        The index at which to add the service.
   */
  addServiceLayer: function (serviceLayer, index) {
    var result = null;

    if (this._tree) {
      var tree = this._tree;
      var self = this;

      result = tree.create_node(
        null,
        {
          icon: false,
          text: serviceLayer.name,
          data: {
            service: serviceLayer,
          },
        },
        index,
        function (node) {
          self._ignoreCheck = true;
          _.each(serviceLayer.getLayers(), function (layer) {
            self._addLayerToTree(layer, node);
          });
          self._ignoreCheck = false;
        }
      );

      var contextMenu = [
        mapContext.getOperation("pan-to-item").withModel(serviceLayer),
        mapContext.getOperation("zoom-to-item").withModel(serviceLayer),
        true,
        mapContext.getOperation("service-select-all").withModel(serviceLayer),
        mapContext.getOperation("service-tabulate-all").withModel(serviceLayer),
        ViewHelper.getCheckAllAction(tree, result),
        ViewHelper.getUncheckAllAction(tree, result),
      ];

      cms.attachContextMenu("#" + result + " > a", contextMenu);
      this._nodeOf(serviceLayer, result);
      this._attachService(serviceLayer);
    }
  },

  /**
   * Adds a layer to the interface.
   * @param {MapLayerBase} layer  The layer to add.
   * @param {MapServiceBase} parent The parent service of the layer to add.
   * @param {object} index  The node that was added.
   */
  addLayer: function (layer, parent, index) {
    return this._addLayerToTree(
      layer,
      parent ? this._nodeOf(parent) : null,
      index
    );
  },

  /**
   * Removes a service from the interface. Any child layers will also be removed.
   * @param  {MapServiceBase} serviceLayer The service to remove.
   */
  removeServiceLayer: function (serviceLayer) {
    this._tree.delete_node(this._nodeOf(serviceLayer));
    this._clearNode(serviceLayer);
  },

  /*this._onLayerMoved = _.bind(function(service, parent, layer, oldIndex, newIndex){*/

  /**
   * Handler for InternalMapService#event:layer-moved
   * @private
   */
  _onServiceLayerMoved: function (service, layer, oldIndex, newIndex) {
    this._onLayerMoved(service, null, layer, oldIndex, newIndex);
  },

  /**
   * Handler for ObjectLayer#event:child-moved
   * @private
   */
  _onLayerChildMoved: function (parent, layer, oldIndex, newIndex) {
    this._onLayerMoved(null, parent, layer, oldIndex, newIndex);
  },

  /**
   * Attaches event handlers to the given service.
   * @param {MapServiceBase} service The service to which events should be attached.
   * @private
   */
  _attachService: function (service) {
    var self = this;

    service.on("layer-moved", this._onServiceLayerMoved, this);

    service.eachLayer(function (layer) {
      self._attachLayer(layer);
    });
  },

  /**
   * Detaches event handlers from the given service.
   * @param {MapServiceBase} service The service from which events should be detached.
   * @private
   */
  _detachService: function (service) {
    var self = this;

    service.off("layer-moved", this._onServiceLayerMoved, this);

    service.eachLayer(function (layer) {
      self._detachLayer(layer);
    });
  },

  /**
   * Attaches event handlers to the given layer.
   * @param {MapLayerBase} layer The layer to which events should be attached.
   * @private
   */
  _attachLayer: function (layer) {
    layer.on("child-moved", this._onLayerChildMoved, this);
  },

  /**
   * Detaches event handlers from the given layer.
   * @param {MapLayerBase} layer The layer from which events should be detached.
   * @private
   */
  _detachLayer: function (layer) {
    layer.off("child-moved", this._onLayerChildMoved, this);
  },

  /**
   * Removes a layer from the interface.
   * @param  {MapLayerBase} layer The layer to remove.
   */
  removeLayer: function (layer) {
    this._tree.delete_node(this._nodeOf(layer));
    this._clearNode(layer);
    this._detachLayer(layer);
  },

  _addLayerToTree: function (layer, defaultParent, index) {
    var tree = this._tree;
    var parentNode = defaultParent || null;
    var self = this;
    var result = tree.create_node(
      parentNode,
      {
        icon: false,
        text: layer.name,
        data: {
          layer: layer,
        },
      },
      index,
      function (node) {
        _.each(layer.children, function (childLayer) {
          self._addLayerToTree(childLayer, parentNode);
        });

        tree.uncheck_node(node);

        self._nodeOf(layer, node);
        self._attachLayer(layer);
      }
    );

    var contextMenu = [
      mapContext.getOperation("pan-to-item").withModel(layer),
      mapContext.getOperation("zoom-to-item").withModel(layer),
      true,
      mapContext.getOperation("layer-select-all").withModel(layer),
      mapContext.getOperation("layer-tabulate-all").withModel(layer),
      mapContext.getOperation("layer-print-all").withModel(layer),
      mapContext.getOperation("download-item").withModel(layer),
      mapContext.getOperation("view-item-files").withModel(layer),
    ];

    if (layer.hasChildren()) {
      contextMenu.push(ViewHelper.getCheckAllAction(tree, result));
      contextMenu.push(ViewHelper.getUncheckAllAction(tree, result));
    }
    cms.attachContextMenu("#" + result + " > a", contextMenu);

    return result;
  },

  /**
   * Reset the text for the node associated with a given model.
   * @param  {object} model The model associated with the node.
   * @param  {string} name  The new node text.
   */
  refreshModelName: function (model, name) {
    this._tree.rename_node(this._nodeOf(model), name);
  },

  /**
   * Reset the text for the node associated with a given layer.
   * @param  {object} layer The layer associated with the node.
   * @param  {string} name  The new node text. If not specified, the `name` property of the `layer` argument will be used.
   */
  refreshLayerName: function (layer, name) {
    this._tree.rename_node(this._nodeOf(layer), name || layer.name);
  },

  /**
   * Reset the text for the node associated with a given service.
   * @param  {object} serviceLayer The service associated with the node.
   * @param  {string} name         The new node text. If not specified, the `name` property of the `serviceLayer` argument will be used.
   */
  refreshServiceLayerName: function (serviceLayer, name) {
    this._tree.rename_node(
      this._nodeOf(serviceLayer),
      name || serviceLayer.name
    );
  },

  _nodeOf: function (item, node) {
    if (node) {
      item._searchNode = node;
    } else {
      return this._tree.get_node(item._searchNode);
    }
  },

  _clearNode: function (item) {
    delete item._searchNode;
  },

  /**
   * Gets the tree node that is the parent of the given item.
   * @param  {object} item The item associated with the node. Either a `MapLayerBase` or a `MapServiceBase`.
   * @return {object} The parent node.
   */
  parentOf: function (item) {
    var node = this._nodeOf(item);

    return this._tree.get_node(node.parent);
  },

  /**
   * Gets the index of the given item amongst its siblings in the tree.
   * @param  {object} item The item associated with the node. Either a `MapLayerBase` or a `MapServiceBase`.
   * @return {Integer} The index of the item in its list of siblings.
   */
  indexOf: function (item) {
    var node = this._nodeOf(item);
    var parent = this.parentOf(item);

    return parent.children.indexOf(node.id);
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/app/views/map/SearchPanelView.js
// module id = 952
// module chunks = 1